import React, { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Maximize2, Minimize2 } from "lucide-react";
import SlideFrame from "./SlideFrame";

const NEXT = new Set(["ArrowRight", "ArrowDown", "PageDown", " "]);
const PREVIOUS = new Set(["ArrowLeft", "ArrowUp", "PageUp"]);

const typing = (target) => {
  const tag = target?.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target?.isContentEditable;
};

/**
 * Scène de présentation : la diapo en cours, flèches et plein écran. `onIndexChange` absent : on ne fait que
 * regarder (la diapo suit quelqu'un d'autre). Flèches du clavier actives quand `keyboard` est vrai.
 */
const FocusStage = forwardRef(function FocusStage(
  { slides = [], index = 0, onIndexChange, keyboard = false, role, locked, onEvents, onState, onReady, frameKey,
    toolbar, notice, className = "" },
  frameRef
) {
  const stageRef = useRef(null);
  const [fullscreen, setFullscreen] = useState(false);
  const count = slides.length;
  const current = slides[Math.max(0, Math.min(index, count - 1))];

  const go = useCallback(
    (next) => {
      if (!onIndexChange || !count) return;
      const bounded = Math.max(0, Math.min(next, count - 1));
      if (bounded !== index) onIndexChange(bounded);
    },
    [onIndexChange, count, index]
  );

  const onKey = useCallback(
    (key) => {
      if (key === "Escape") return;
      if (NEXT.has(key)) go(index + 1);
      else if (PREVIOUS.has(key)) go(index - 1);
      else if (key === "Home") go(0);
      else if (key === "End") go(count - 1);
    },
    [go, index, count]
  );

  useEffect(() => {
    if (!keyboard || !onIndexChange) return undefined;
    const listener = (event) => {
      if (typing(event.target) || event.altKey || event.ctrlKey || event.metaKey) return;
      if (NEXT.has(event.key) || PREVIOUS.has(event.key) || event.key === "Home" || event.key === "End") {
        event.preventDefault();
        onKey(event.key);
      }
    };
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, [keyboard, onIndexChange, onKey]);

  useEffect(() => {
    const sync = () => setFullscreen(document.fullscreenElement === stageRef.current);
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else stageRef.current?.requestFullscreen?.();
  };

  const navButton = "inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:border-indigo-200 hover:text-indigo-700 disabled:opacity-40";
  return (
    <div
      ref={stageRef}
      className={`flex flex-col gap-2 ${fullscreen ? "h-screen w-screen bg-slate-900 p-3" : ""} ${className}`}
    >
      <div className={fullscreen ? "min-h-0 flex-1" : ""}>
        <SlideFrame
          key={frameKey}
          ref={frameRef}
          document={current?.document}
          title={current?.title}
          role={role}
          locked={locked}
          fit={fullscreen ? "contain" : "width"}
          onEvents={onEvents}
          onState={onState}
          onReady={onReady}
          onKey={keyboard ? onKey : undefined}
          className={fullscreen ? "" : "rounded-lg border border-slate-200 bg-slate-100"}
        />
      </div>
      <div className={`flex flex-wrap items-center gap-2 ${fullscreen ? "text-slate-200" : "text-slate-600"}`}>
        {onIndexChange && (
          <button type="button" className={navButton} onClick={() => go(index - 1)} disabled={index <= 0}
                  aria-label="Diapo précédente">
            <ChevronLeft size={16} />
          </button>
        )}
        <span className="min-w-[4rem] text-center text-xs font-semibold tabular-nums">
          {count ? `${Math.min(index, count - 1) + 1} / ${count}` : "0 / 0"}
        </span>
        {onIndexChange && (
          <button type="button" className={navButton} onClick={() => go(index + 1)} disabled={index >= count - 1}
                  aria-label="Diapo suivante">
            <ChevronRight size={16} />
          </button>
        )}
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{current?.title || ""}</span>
        {notice}
        {toolbar}
        <button type="button" className={navButton} onClick={toggleFullscreen}
                aria-label={fullscreen ? "Quitter le plein écran" : "Plein écran"}
                title={fullscreen ? "Quitter le plein écran (Échap)" : "Plein écran"}>
          {fullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
        </button>
      </div>
    </div>
  );
});

export default FocusStage;
